// src/screens/CollaborationManagement.jsx
import 'bootstrap-icons/font/bootstrap-icons.css';
import { useMemo, useState, useEffect } from 'react';
import { useLogs } from '../context/LogContext';
import Swal from 'sweetalert2';
import '../css/Dashboard.css';
import '../css/RoleManagement.css';
import useIdleLogout from "../hooks/useIdleLogout";

/* ============================== API CONFIG (from .env) ============================== */
// Prefer explicit endpoint from .env; fallback to new backend domain.
const ENV_COLLAB_URL = (process.env.REACT_APP_COLLABORATORS_API_URL || '').trim();

/** remove trailing slash to avoid // when appending /:id */
const trimTrailingSlash = (s = '') => s.replace(/\/+$/, '');

// Final collection endpoint:
// https://backend.tabdhey.bt/admin/api/admin-collaborators
const ENDPOINT = trimTrailingSlash(
  ENV_COLLAB_URL || 'https://backend.tabdhey.bt/admin/api/admin-collaborators'
);

/* --------- auth helpers (read whoever actually logged in) ---------- */
function readAuthedUser() {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function getAdminId() {
  const u = readAuthedUser() || {};
  return String(u.id ?? u.user_id ?? u.admin_id ?? '').trim() || '';
}

function getAdminName() {
  const u = readAuthedUser() || {};
  const firstLast = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  return u.full_name || u.user_name || u.name || firstLast || '';
}

/* --------- bearer token helper ---------- */
function getBearerToken() {
  const directToken =
    localStorage.getItem('token') ||
    localStorage.getItem('adminToken') ||
    localStorage.getItem('accessToken') ||
    localStorage.getItem('authToken') ||
    sessionStorage.getItem('token') ||
    sessionStorage.getItem('adminToken') ||
    sessionStorage.getItem('accessToken') ||
    sessionStorage.getItem('authToken');

  if (directToken) return directToken;

  const user = readAuthedUser() || {};

  return (
    user.token ||
    user.adminToken ||
    user.accessToken ||
    user.access_token ||
    user.authToken ||
    user.jwt ||
    ''
  );
}

function getAuthHeaders(hasBody = false) {
  const token = getBearerToken();

  const headers = {
    Accept: 'application/json',
  };

  if (hasBody) {
    headers['Content-Type'] = 'application/json';
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

/* ----------------------- small ui helpers ------------------------ */
const isBlank = (v) => !v || String(v).trim() === '';

const openGmailWeb = ({ to, subject = '', body = '' }) => {
  const url = new URL('https://mail.google.com/mail/');
  url.searchParams.set('view', 'cm');
  url.searchParams.set('fs', '1');

  if (!isBlank(to)) url.searchParams.set('to', to);
  if (!isBlank(subject)) url.searchParams.set('su', subject);
  if (!isBlank(body)) url.searchParams.set('body', body);

  window.open(url.toString(), '_blank', 'noopener,noreferrer');
};

const openOutlookWeb = ({ to, subject = '', body = '' }) => {
  const url = new URL('https://outlook.office.com/mail/deeplink/compose');

  if (!isBlank(to)) url.searchParams.set('to', to);
  if (!isBlank(subject)) url.searchParams.set('subject', subject);
  if (!isBlank(body)) url.searchParams.set('body', body);

  window.open(url.toString(), '_blank', 'noopener,noreferrer');
};

const sanitizePhone = (p) => (p || '').replace(/\D/g, '');

const openWhatsApp = ({ phone, text = '' }) => {
  const digits = sanitizePhone(phone);
  if (!digits) return;

  const url = new URL(`https://wa.me/${digits}`);
  if (!isBlank(text)) url.searchParams.set('text', text);

  window.open(url.toString(), '_blank', 'noopener,noreferrer');
};

async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text || '');

    Swal.fire({
      icon: 'success',
      title: 'Copied!',
      text: 'Text copied to clipboard',
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 2000,
      timerProgressBar: true,
    });
  } catch {
    Swal.fire({
      icon: 'error',
      title: 'Copy Failed',
      text: 'Failed to copy to clipboard',
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 3000,
    });
  }
}

/* ============================== API LAYER ============================== */
/**
 * Backend now requires:
 * Authorization: Bearer <token>
 *
 * This applies to:
 * POST /api/admin-collaborators
 * PUT /api/admin-collaborators/:id
 * DELETE /api/admin-collaborators/:id
 *
 * GET also sends the Bearer token safely.
 */
function buildAuthPayload() {
  const uid = getAdminId();
  const name = getAdminName();

  return {
    auth: {
      user_id: uid ? Number(uid) || uid : undefined,
      admin_name: name || undefined,
    },
  };
}

async function apiFetch(path, { method = 'GET', body } = {}) {
  const upper = (method || 'GET').toUpperCase();
  const hasBody = upper !== 'GET';

  const opts = {
    method: upper,
    headers: getAuthHeaders(hasBody),
  };

  if (upper !== 'GET') {
    const withAuth = {
      ...buildAuthPayload(),
      ...(body || {}),
    };

    opts.body = JSON.stringify(withAuth);
  }

  const res = await fetch(path, opts);

  if (!res.ok) {
    const text = await res.text().catch(() => '');

    if (res.status === 401) {
      throw new Error('Unauthorized. Please login again.');
    }

    if (res.status === 403) {
      throw new Error('Forbidden. You do not have permission to perform this action.');
    }

    throw new Error(`HTTP ${res.status} ${res.statusText} — ${text || 'Request failed'}`);
  }

  if (res.status === 204) return null;

  const ct = res.headers.get('content-type') || '';
  return ct.includes('application/json') ? await res.json() : await res.text();
}

const collaborationAPI = {
  async getAll() {
    const data = await apiFetch(ENDPOINT, { method: 'GET' });

    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.data)) return data.data;

    return [];
  },

  async getOne(id) {
    if (!id) throw new Error('Missing id');

    const data = await apiFetch(`${ENDPOINT}/${id}`, { method: 'GET' });

    if (Array.isArray(data)) return data[0] ?? null;
    if (data && Array.isArray(data.data)) return data.data[0] ?? null;
    if (data && typeof data.data === 'object') return data.data;

    return data ?? null;
  },

  async create(payload) {
    return await apiFetch(ENDPOINT, {
      method: 'POST',
      body: payload,
    });
  },

  async update(id, payload) {
    if (!id) throw new Error('Missing id');

    return await apiFetch(`${ENDPOINT}/${id}`, {
      method: 'PUT',
      body: payload,
    });
  },

  async remove(id) {
    if (!id) throw new Error('Missing id');

    return await apiFetch(`${ENDPOINT}/${id}`, {
      method: 'DELETE',
      body: {},
    });
  },
};

/* ============================== COMPONENT ============================== */
export default function CollaborationManagement() {
  useIdleLogout();

  const { logActivity } = useLogs();

  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [form, setForm] = useState({
    name: '',
    contact: '',
    email: '',
    service: '',
    role: '',
    address: '',
    cid: '',
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [editingId, setEditingId] = useState(null);

  const [showContact, setShowContact] = useState(false);
  const [contactTarget, setContactTarget] = useState(null);
  const [contactTab, setContactTab] = useState('email');

  const [emailForm, setEmailForm] = useState({ to: '', subject: '', body: '' });
  const [callForm, setCallForm] = useState({ phone: '', message: '' });
  const [smsForm, setSmsForm] = useState({ phone: '', text: '' });

  /* -------- initial load -------- */
  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);

      try {
        const list = await collaborationAPI.getAll();
        setPeople(list);
      } catch (e) {
        setError(e.message || String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const showSweetAlertSuccess = (message) => {
    Swal.fire({
      icon: 'success',
      title: 'Success!',
      text: message,
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 2500,
      timerProgressBar: true,
    });
  };

  const showSweetAlertError = (message) => {
    Swal.fire({
      icon: 'error',
      title: 'Error!',
      text: message,
      confirmButtonColor: '#d33',
    });
  };

  const payloadFromForm = () => ({
    full_name: form.name.trim(),
    contact: form.contact.trim(),
    email: form.email.trim(),
    service: form.service.trim(),
    role: form.role.trim(),
    current_address: form.address.trim(),
    cid: form.cid.trim(),
  });

  const refreshAll = async () => {
    const list = await collaborationAPI.getAll();
    setPeople(list);
  };

  const addPerson = async (e) => {
    e.preventDefault();

    const needed = ['name', 'contact', 'email', 'service', 'role', 'address', 'cid'];

    if (needed.some((k) => isBlank(form[k]))) {
      showSweetAlertError('Please fill all required fields');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const body = payloadFromForm();

      if (editingId) {
        await collaborationAPI.update(editingId, body);
        await refreshAll();

        logActivity?.(`Updated collaborator "${form.name}"`);
        showSweetAlertSuccess('Collaborator updated successfully!');
        setEditingId(null);
      } else {
        await collaborationAPI.create(body);
        await refreshAll();

        logActivity?.(`Added collaborator "${form.name}"`);
        showSweetAlertSuccess('Collaborator added successfully!');
      }

      setForm({
        name: '',
        contact: '',
        email: '',
        service: '',
        role: '',
        address: '',
        cid: '',
      });
    } catch (err) {
      showSweetAlertError(`Operation failed: ${err.message || err}`);
      setError(err.message || String(err));
      logActivity?.(`Save error: ${err.message || err}`);
    } finally {
      setLoading(false);
    }
  };

  const editPerson = (person) => {
    const personId = person.collaborator_id ?? person.id;

    if (!personId) {
      showSweetAlertError('Cannot edit: Invalid collaborator ID');
      return;
    }

    setEditingId(personId);

    setForm({
      name: person.full_name || person.name || '',
      contact: person.contact || '',
      email: person.email || '',
      service: person.service || '',
      role: person.role || '',
      address: person.current_address || person.address || '',
      cid: person.cid || '',
    });

    document.querySelector('form')?.scrollIntoView({ behavior: 'smooth' });
    showSweetAlertSuccess(`Now editing: ${person.full_name || person.name}`);
  };

  const deletePerson = async (idOrObj) => {
    const id =
      typeof idOrObj === 'number'
        ? idOrObj
        : idOrObj?.collaborator_id ?? idOrObj?.id;

    const person = people.find(
      (p) => (p.collaborator_id ?? p.id) === id
    );

    if (!person) {
      showSweetAlertError('Collaborator not found');
      return;
    }

    const ok = await Swal.fire({
      title: 'Are you sure?',
      text: `Delete "${person.full_name || person.name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      reverseButtons: true,
    });

    if (!ok.isConfirmed) return;

    setLoading(true);

    try {
      await collaborationAPI.remove(id);
      await refreshAll();

      logActivity?.(`Deleted collaborator "${person.full_name || person.name}"`);
      showSweetAlertSuccess('Collaborator deleted successfully!');
    } catch (err) {
      showSweetAlertError(`Delete failed: ${err.message || err}`);
      setError(err.message || String(err));
      logActivity?.(`Delete error: ${err.message || err}`);
    } finally {
      setLoading(false);
    }
  };

  const openContact = (person) => {
    setContactTarget(person);
    setContactTab('email');

    setEmailForm({
      to: person.email || '',
      subject: `Hello ${person.full_name || person.name}`,
      body: '',
    });

    setCallForm({
      phone: person.contact || '',
      message: `Hi ${person.full_name || person.name}, can we talk regarding your role (${person.role})?`,
    });

    setSmsForm({
      phone: person.contact || '',
      text: `Hi ${person.full_name || person.name}, this is Admin from SuperApp.`,
    });

    setShowContact(true);
  };

  const filteredPeople = useMemo(() => {
    const q = (searchQuery || '').toLowerCase();

    return (people || []).filter((p) =>
      [
        p.full_name || p.name,
        p.contact,
        p.email,
        p.service,
        p.role,
        p.current_address || p.address,
        p.cid,
      ].some((field) => field && field.toString().toLowerCase().includes(q))
    );
  }, [people, searchQuery]);

  const displayedPeople = filteredPeople;

  const doEmailGmail = () => {
    openGmailWeb(emailForm);

    if (contactTarget) {
      Swal.fire({
        icon: 'success',
        title: 'Opening Gmail…',
        text: `Composing mail to ${contactTarget.full_name || contactTarget.name}`,
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 1800,
      });

      // optional log hook
      // logActivity?.(`Opened Gmail Web to email ${contactTarget.full_name || contactTarget.name}`);
    }
  };

  const doEmailOutlook = () => {
    openOutlookWeb(emailForm);

    if (contactTarget) {
      Swal.fire({
        icon: 'success',
        title: 'Opening Outlook…',
        text: `Composing mail to ${contactTarget.full_name || contactTarget.name}`,
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 1800,
      });

      // logActivity?.(`Opened Outlook Web to email ${contactTarget.full_name || contactTarget.name}`);
    }
  };

  const copyEmail = () => copyToClipboard(emailForm.to);

  const doWhatsAppFromCall = () =>
    openWhatsApp({
      phone: callForm.phone,
      text: callForm.message,
    });

  const copyPhone = () => copyToClipboard(callForm.phone);

  const doWhatsAppFromSms = () =>
    openWhatsApp({
      phone: smsForm.phone,
      text: smsForm.text,
    });

  const copySms = () => copyToClipboard(`${smsForm.phone} — ${smsForm.text}`);

  const cancelEdit = () => {
    setEditingId(null);

    setForm({
      name: '',
      contact: '',
      email: '',
      service: '',
      role: '',
      address: '',
      cid: '',
    });

    setError(null);

    Swal.fire({
      icon: 'success',
      title: 'Edit cancelled',
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 1500,
    });
  };

  return (
    <div className="p-4 flex-grow-1">
      <div className="card p-4">
        <h4 className="section-title mb-3">Add & Manage Collaborators</h4>

        {success && (
          <div className="alert alert-success alert-dismissible fade show" role="alert">
            <strong>Success!</strong> {success}
            <button type="button" className="btn-close" onClick={() => setSuccess(null)}></button>
          </div>
        )}

        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            <strong>Error:</strong> {error}
            <button type="button" className="btn-close" onClick={() => setError(null)}></button>
          </div>
        )}

        {loading && (
          <div className="text-center py-3">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted mt-2">Processing your request...</p>
          </div>
        )}

        {/* Add/Edit Form */}
        <form className="row g-3 mb-4" onSubmit={addPerson}>
          <div className="col-md-4">
            <label className="form-label">Full Name *</label>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              className="form-control"
              placeholder="Enter name"
              required
              disabled={loading}
            />
          </div>

          <div className="col-md-4">
            <label className="form-label">Contact *</label>
            <input
              type="text"
              name="contact"
              value={form.contact}
              onChange={handleChange}
              className="form-control"
              placeholder="Enter phone number"
              required
              disabled={loading}
            />
          </div>

          <div className="col-md-4">
            <label className="form-label">Email *</label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              className="form-control"
              placeholder="Enter email"
              required
              disabled={loading}
            />
          </div>

          <div className="col-md-4">
            <label className="form-label">Service *</label>
            <input
              type="text"
              name="service"
              value={form.service}
              onChange={handleChange}
              className="form-control"
              placeholder="Enter service/company"
              required
              disabled={loading}
            />
          </div>

          <div className="col-md-4">
            <label className="form-label">Role *</label>
            <input
              type="text"
              name="role"
              value={form.role}
              onChange={handleChange}
              className="form-control"
              placeholder="Enter role"
              required
              disabled={loading}
            />
          </div>

          <div className="col-md-4">
            <label className="form-label">Current Address *</label>
            <input
              type="text"
              name="address"
              value={form.address}
              onChange={handleChange}
              className="form-control"
              placeholder="Enter address"
              required
              disabled={loading}
            />
          </div>

          <div className="col-md-4">
            <label className="form-label">CID (Citizen ID) *</label>
            <input
              type="text"
              name="cid"
              value={form.cid}
              onChange={handleChange}
              className="form-control"
              placeholder="Enter CID"
              required
              disabled={loading}
            />
          </div>

          <div className="col-12">
            {editingId ? (
              <div className="d-grid gap-2">
                <button
                  type="submit"
                  className="btn btn-success btn-lg w-100"
                  disabled={loading}
                  style={{ borderRadius: '30px' }}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-lg me-2"></i>
                      Save Edit
                    </>
                  )}
                </button>

                <button
                  type="button"
                  className="btn btn-danger btn-lg w-100"
                  onClick={cancelEdit}
                  disabled={loading}
                  style={{ borderRadius: '30px' }}
                >
                  <i className="bi bi-x-lg me-2"></i>
                  Cancel Edit
                </button>
              </div>
            ) : (
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Adding...
                  </>
                ) : (
                  <>
                    <i className="bi bi-plus-lg me-2"></i>
                    Add Collaborator
                  </>
                )}
              </button>
            )}
          </div>
        </form>

        {/* Search Bar */}
        <div className="search-bar-wrapper mb-3 position-relative">
          <i className="bi bi-search search-icon"></i>
          <input
            type="text"
            className="form-control ps-5"
            placeholder="Search by name, email, service, role, or CID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            disabled={loading}
          />
        </div>

        {/* Collaborator Table */}
        <div className="table-responsive">
          <table className="table role-table">
            <thead>
              <tr>
                <th style={{ width: '5%' }}>SI.No</th>
                <th style={{ width: '50%' }}>Name</th>
                <th style={{ width: '10%' }}>Contact</th>
                <th style={{ width: '35%' }}>Email</th>
                <th style={{ width: '20%' }}>Service</th>
                <th style={{ width: '30%' }}>Role</th>
                <th style={{ width: '20%' }}>Current Address</th>
                <th style={{ width: '10%' }}>CID</th>
                <th className="text-center" style={{ width: '5%' }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {displayedPeople.map((person, idx) => (
                <tr
                  key={person.collaborator_id ?? person.id ?? idx}
                  className={editingId === (person.collaborator_id ?? person.id) ? 'table-warning' : ''}
                >
                  <td>{idx + 1}</td>
                  <td>{person.full_name || person.name}</td>
                  <td>{person.contact}</td>
                  <td>{person.email}</td>
                  <td>{person.service}</td>
                  <td>{person.role}</td>
                  <td>{person.current_address || person.address}</td>
                  <td>{person.cid}</td>

                  <td className="action-buttons text-center">
                    <button
                      className="icon-btn edit-btn"
                      title="Edit"
                      onClick={() => editPerson(person)}
                      disabled={loading}
                    >
                      <i className="bi bi-pencil-fill"></i>
                    </button>

                    <button
                      className="icon-btn contact-btn"
                      title="Contact"
                      onClick={() => openContact(person)}
                      disabled={loading}
                    >
                      <i className="bi bi-envelope-fill"></i>
                    </button>

                    <button
                      className="icon-btn delete-btn"
                      title="Delete"
                      onClick={() => deletePerson(person)}
                      disabled={loading}
                    >
                      <i className="bi bi-trash-fill"></i>
                    </button>
                  </td>
                </tr>
              ))}

              {displayedPeople.length === 0 && !loading && (
                <tr>
                  <td colSpan="9" className="text-center text-muted">
                    {people.length === 0
                      ? 'No collaborators found. Add your first collaborator above.'
                      : 'No records match your search.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Contact Modal */}
      {showContact && (
        <div className="contact-modal-backdrop" onClick={() => setShowContact(false)}>
          <div className="contact-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header d-flex justify-content-between align-items-center">
              <h5 className="modal-title">
                Contact {contactTarget?.full_name || contactTarget?.name}
              </h5>

              <button
                className="btn-close"
                onClick={() => setShowContact(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            {/* Tabs */}
            <div className="modal-tabs d-flex gap-2 p-2">
              <button
                className={`tab-btn ${contactTab === 'email' ? 'active' : ''}`}
                onClick={() => setContactTab('email')}
              >
                ✉️ Email
              </button>

              <button
                className={`tab-btn ${contactTab === 'call' ? 'active' : ''}`}
                onClick={() => setContactTab('call')}
              >
                ☎️ Phone Call
              </button>

              <button
                className={`tab-btn ${contactTab === 'sms' ? 'active' : ''}`}
                onClick={() => setContactTab('sms')}
              >
                💬 SMS
              </button>
            </div>

            <div className="modal-body p-3">
              {contactTab === 'email' && (
                <>
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label">To</label>
                      <input
                        className="form-control"
                        type="email"
                        value={emailForm.to}
                        onChange={(e) =>
                          setEmailForm((f) => ({
                            ...f,
                            to: e.target.value,
                          }))
                        }
                        placeholder="recipient@example.com"
                      />
                    </div>

                    <div className="col-12">
                      <label className="form-label">Subject</label>
                      <input
                        className="form-control"
                        type="text"
                        value={emailForm.subject}
                        onChange={(e) =>
                          setEmailForm((f) => ({
                            ...f,
                            subject: e.target.value,
                          }))
                        }
                        placeholder="Subject"
                      />
                    </div>

                    <div className="col-12">
                      <label className="form-label">Body</label>
                      <textarea
                        className="form-control"
                        rows={5}
                        value={emailForm.body}
                        onChange={(e) =>
                          setEmailForm((f) => ({
                            ...f,
                            body: e.target.value,
                          }))
                        }
                        placeholder="Write your message…"
                      />
                    </div>
                  </div>

                  <div className="d-flex flex-wrap gap-2 mt-3">
                    <button
                      className="btn btn-primary"
                      onClick={doEmailGmail}
                      disabled={isBlank(emailForm.to)}
                    >
                      Open in Gmail Web
                    </button>

                    <button
                      className="btn btn-outline-primary"
                      onClick={doEmailOutlook}
                      disabled={isBlank(emailForm.to)}
                    >
                      Open in Outlook Web
                    </button>

                    <button
                      className="btn btn-light"
                      onClick={copyEmail}
                      disabled={isBlank(emailForm.to)}
                    >
                      Copy address
                    </button>
                  </div>
                </>
              )}

              {contactTab === 'call' && (
                <>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Phone</label>
                      <input
                        className="form-control"
                        type="tel"
                        value={callForm.phone}
                        onChange={(e) =>
                          setCallForm((f) => ({
                            ...f,
                            phone: e.target.value,
                          }))
                        }
                        placeholder="Phone number"
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label">Message (WhatsApp)</label>
                      <input
                        className="form-control"
                        type="text"
                        value={callForm.message}
                        onChange={(e) =>
                          setCallForm((f) => ({
                            ...f,
                            message: e.target.value,
                          }))
                        }
                        placeholder="Optional message"
                      />
                    </div>
                  </div>

                  <div className="d-flex flex-wrap gap-2 mt-3">
                    <button
                      className="btn btn-success"
                      onClick={doWhatsAppFromCall}
                      disabled={isBlank(callForm.phone)}
                    >
                      WhatsApp Web
                    </button>

                    <button
                      className="btn btn-light"
                      onClick={copyPhone}
                      disabled={isBlank(callForm.phone)}
                    >
                      Copy number
                    </button>
                  </div>
                </>
              )}

              {contactTab === 'sms' && (
                <>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Phone</label>
                      <input
                        className="form-control"
                        type="tel"
                        value={smsForm.phone}
                        onChange={(e) =>
                          setSmsForm((f) => ({
                            ...f,
                            phone: e.target.value,
                          }))
                        }
                        placeholder="Phone number"
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label">Text</label>
                      <input
                        className="form-control"
                        type="text"
                        value={smsForm.text}
                        onChange={(e) =>
                          setSmsForm((f) => ({
                            ...f,
                            text: e.target.value,
                          }))
                        }
                        placeholder="Your message"
                      />
                    </div>
                  </div>

                  <div className="d-flex flex-wrap gap-2 mt-3">
                    <button
                      className="btn btn-success"
                      onClick={doWhatsAppFromSms}
                      disabled={isBlank(smsForm.phone)}
                    >
                      WhatsApp Web
                    </button>

                    <button
                      className="btn btn-light"
                      onClick={copySms}
                      disabled={isBlank(smsForm.phone)}
                    >
                      Copy text
                    </button>
                  </div>
                </>
              )}
            </div>

            <div className="modal-footer p-3">
              <button
                className="btn btn-secondary"
                onClick={() => setShowContact(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <footer className="dashboard-footer text-center py-2 mt-auto">
        <small>
          © {new Date().getFullYear()} <strong>སྟབས་བདེ། </strong> Admin • All rights reserved
        </small>
      </footer>
    </div>
  );
}